import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { Description, FileDownload } from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';

import {
  classifyFlowgateResultError,
  downloadFlowgateReportLatest,
  useFlowgateReports,
  useFlowgateValidationStats,
  useSubmitFlowgateReport,
  type FlowgateReportStatus,
  type FlowgateReportType,
} from '@/api/flowgate';

const REPORT_TYPES: { value: FlowgateReportType; label: string }[] = [
  { value: 'TEST', label: 'Test' },
  { value: 'CANCER', label: 'Cancer' },
  { value: 'ANCESTRY', label: 'Ancestry' },
  { value: 'HORIZON', label: 'Horizon' },
];

const REPORT_STATUS_COLOR: Record<
  FlowgateReportStatus,
  'warning' | 'success' | 'error'
> = {
  PENDING: 'warning',
  SUCCESS: 'success',
  FAILED: 'error',
};

const FlowgateReportStep = ({ resultId }: { resultId?: string }) => {
  const { data: stats } = useFlowgateValidationStats(resultId);
  const { data: reports = [], refetch: refetchReports } =
    useFlowgateReports(resultId);
  const submitReport = useSubmitFlowgateReport();

  const [reportType, setReportType] = useState<FlowgateReportType>('TEST');
  const [downloading, setDownloading] = useState(false);

  // The report includes every workbench row that has not been rejected.
  const reportableCount = Math.max(
    0,
    (stats?.total ?? 0) - (stats?.rejected ?? 0),
  );

  const surfaceError = (err: unknown) => {
    const info = classifyFlowgateResultError(err);
    enqueueSnackbar(info.message, {
      variant: info.kind === 'not_ready' ? 'warning' : 'error',
    });
  };

  const handleGenerate = () => {
    if (!resultId) return;
    submitReport.mutate(
      { resultId, reportType },
      {
        onSuccess: () => {
          enqueueSnackbar(
            'Report submitted. It will be ready to download shortly.',
            { variant: 'success' },
          );
          refetchReports();
        },
        onError: surfaceError,
      },
    );
  };

  const handleDownload = async () => {
    if (!resultId) return;
    setDownloading(true);
    try {
      const resp = await downloadFlowgateReportLatest(resultId);
      const url = window.URL.createObjectURL(resp.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType.toLowerCase()}_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const info = classifyFlowgateResultError(err);
      enqueueSnackbar(
        info.kind === 'not_found'
          ? 'No completed report yet. Generate one first.'
          : info.message,
        { variant: info.kind === 'not_found' ? 'info' : 'error' },
      );
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ md: 'flex-start' }}
        sx={{ mb: 2.5 }}
      >
        <Box>
          <Typography variant="h2">Generate report</Typography>
          <Typography variant="body2" color="text.secondary">
            Submit workbench variants that have not been rejected and track the
            report status here.
          </Typography>
        </Box>
        <Chip
          size="small"
          color={reportableCount > 0 ? 'success' : 'default'}
          variant="outlined"
          label={`${reportableCount} to report`}
        />
      </Stack>

      {stats && (
        <Box
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 1.5,
            bgcolor: 'background.default',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Validation summary
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              label={`Total ${stats.total}`}
              variant="outlined"
              size="small"
            />
            <Chip label={`Pending ${stats.pending}`} size="small" />
            <Chip
              label={`Rejected ${stats.rejected}`}
              color="error"
              size="small"
            />
          </Stack>
        </Box>
      )}

      {reportableCount === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No variants to report yet. Add variants on the workbench. Rejected
          variants are left out.
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {reportableCount} variant{reportableCount === 1 ? '' : 's'} will be
        included in the report.
      </Typography>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ sm: 'center' }}
        flexWrap="wrap"
        sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'background.default' }}
      >
        <Select
          size="small"
          value={reportType}
          onChange={(e) => setReportType(e.target.value as FlowgateReportType)}
          sx={{ minWidth: 140 }}
        >
          {REPORT_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </Select>
        <Button
          variant="contained"
          startIcon={<Description />}
          onClick={handleGenerate}
          disabled={reportableCount === 0 || submitReport.isPending}
        >
          Generate Report
        </Button>
        <Button
          variant="outlined"
          startIcon={
            downloading ? <CircularProgress size={16} /> : <FileDownload />
          }
          onClick={handleDownload}
          disabled={downloading}
        >
          Download Report
        </Button>
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Report generation is asynchronous — status updates automatically
          below.
        </Typography>
      </Box>

      {reports.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Reports
          </Typography>
          <Stack spacing={1}>
            {reports.map((r) => (
              <Stack
                key={r.id}
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  bgcolor: 'background.default',
                }}
              >
                <Chip
                  size="small"
                  label={r.status}
                  color={REPORT_STATUS_COLOR[r.status]}
                />
                <Typography variant="body2">{r.report_type}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(r.created_at).toLocaleString()}
                </Typography>
                {r.status === 'PENDING' && <CircularProgress size={14} />}
                {r.status === 'FAILED' && r.error_message && (
                  <Typography variant="caption" color="error">
                    {r.error_message}
                  </Typography>
                )}
                <Box sx={{ flexGrow: 1 }} />
                {r.status === 'SUCCESS' && (
                  <Button
                    size="small"
                    startIcon={<FileDownload />}
                    onClick={handleDownload}
                    disabled={downloading}
                  >
                    Download
                  </Button>
                )}
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  );
};

export default FlowgateReportStep;
